/**
 * Shared client for async filing-materials ZIP exports.
 *
 * The backend builds these ZIPs on a worker (they can exceed Cloud Run's
 * 32 MiB response cap) and exposes an export record that eventually carries a
 * signed GCS download URL. The client polls the record until it is ready,
 * then downloads straight from GCS.
 */

export type FilingMaterialsExport = {
  export_id: string;
  status: "processing" | "ready" | "failed";
  download_url?: string | null;
  download_name?: string | null;
  error?: string | null;
};

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const pollUntilReady = async (
  fetchExport: () => Promise<FilingMaterialsExport>,
  deadline: number
): Promise<FilingMaterialsExport> => {
  const exportRecord = await fetchExport();
  if (exportRecord.status !== "processing") {
    return exportRecord;
  }
  if (Date.now() >= deadline) {
    throw new Error("Timed out preparing the download");
  }
  await sleep(POLL_INTERVAL_MS);
  return pollUntilReady(fetchExport, deadline);
};

/**
 * Poll an export record until it leaves "processing" or the timeout elapses.
 */
export const pollFilingMaterialsExport = async (
  fetchExport: () => Promise<FilingMaterialsExport>
): Promise<FilingMaterialsExport> => {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  return pollUntilReady(fetchExport, deadline);
};

/**
 * Navigate the browser to the signed URL. The URL carries a
 * Content-Disposition: attachment override, so this downloads rather than
 * navigates away.
 */
export const triggerSignedUrlDownload = (downloadUrl: string) => {
  const link = document.createElement("a");
  link.href = downloadUrl;
  document.body.appendChild(link);
  link.click();
  link.remove();
};
